import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Bookmark, Clapperboard, Heart, Play, Search, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import CreditConfirmModal from '../components/fragmentador/CreditConfirmModal';
import ImageModelPicker, { defaultImageModel } from '../components/image/ImageModelPicker';
import VideoModelPicker, { type VideoModelPickerValue } from '../components/video/VideoModelPicker';
import { ChipsSkeleton, GallerySkeleton, ViewerPanelSkeleton } from '../components/ExploreSkeletons';
import { uploadToGallery } from '../lib/mediaGallery';
import { STAGE_JOBS_TIMEOUT_MS, toMediaUrl, waitForJob, waitForStageJobs } from '../lib/jobsApi';
import { quoteTemplateCredits } from '../lib/templateQuote';
import ExploreAspectField from '../components/explore/ExploreAspectField';
import { aspectFromMode, type ExploreAspectMode } from '../lib/exploreAspect';
import { assignFlowImages, orderedImageSlots } from '../lib/exploreMatch';
import { readImageSize } from '../lib/stillNormalize';
import { toMediaUrl as mediaUrl } from '../lib/mediaUrl';
import { getVideoModelById, requiresDrivingAudio } from '../lib/videoModelCatalog';
import {
  fetchCategories,
  fetchHomeSlots,
  fetchMyFlags,
  fetchPublishedTemplates,
  fetchPublishedTemplate,
  recordTemplateView,
  remixTemplate,
  setFlag,
  type ExploreCategory,
  type ExploreSlot,
  type ExploreTemplate,
  type FeedSort,
} from '../lib/exploreTemplates';
import '../explore.css';

function imageFile(list: FileList | null): File | null {
  return Array.from(list || []).find((item) => item.type.startsWith('image/')) || null;
}

/** Click or drop an image. The native file control stays in the hit area, invisible. */
function ExploreImageDrop(props: {
  id: string;
  name: string;
  file: File | null;
  seededUrl: string;
  onFile: (file: File | null) => void;
  onClearSeed: () => void;
}) {
  const [over, setOver] = useState(false);
  const preview = useMemo(() => (props.file ? URL.createObjectURL(props.file) : ''), [props.file]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  const shown = preview || props.seededUrl;
  const take = (list: FileList | null) => {
    const file = imageFile(list);
    if (file) props.onFile(file);
  };
  return (
    <div
      className={`explore-drop${over ? ' is-over' : ''}`}
      onDragEnter={(e) => { e.preventDefault(); setOver(true); }}
      onDragOverCapture={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={(e) => { if (e.currentTarget.contains(e.relatedTarget as Node)) return; setOver(false); }}
      onDropCapture={(e) => { e.preventDefault(); setOver(false); take(e.dataTransfer.files); }}
    >
      {shown ? <img src={shown} alt="" /> : (
        <span className="explore-drop-hint">Arraste uma imagem ou clique para escolher</span>
      )}
      <input
        id={props.id}
        name={props.name}
        type="file"
        accept="image/*"
        className="explore-drop-input"
        aria-label={shown ? 'Trocar imagem' : 'Escolher imagem'}
        onChange={(e) => { take(e.target.files); e.target.value = ''; }}
      />
      {shown && (
        <button
          type="button"
          className="explore-seed-clear"
          onClick={() => { props.onFile(null); props.onClearSeed(); }}
        >
          Não usar esta imagem
        </button>
      )}
    </div>
  );
}

type Phase = 'stages' | 'queued' | 'processing' | 'done' | 'failed';

export type CanvasRemixResult = {
  jobId: string;
  credits: number;
  title: string;
  aspectRatio: string;
  lockModel: boolean;
  model: {
    videoModelId: string;
    resolutionId: string;
    durationSeconds: number;
    generateAudio: boolean;
  };
  imageUrls: string[];
  imageLabels: string[];
  /** Intermediate stage images, when the template uses stages. */
  stageUrls?: string[];
  /** False when the template only sends stages into the video (don't clone user photos on the board). */
  includeUserImages?: boolean;
  /**
   * Canvas handoff: `preparing` closes the modal and shows loading on the board while stages run;
   * `ready` attaches the remix job; `failed` marks the pending card.
   */
  phase?: 'preparing' | 'ready' | 'failed';
  /** True while Explore stages are still rendering before the video job exists. */
  awaitingStages?: boolean;
  /** How many loading image cards to drop on the board while stages run. */
  stageCount?: number;
  error?: string;
};

type VideoTrack = {
  jobId: string;
  templateId: string;
  title: string;
  credits: number;
  phase: Phase;
  url: string | null;
  error: string | null;
  withStages?: boolean;
  /** When phase is stages: how many image jobs finished. */
  stageReady?: number;
  stageTotal?: number;
};

const STEPS: { id: Phase; label: string }[] = [
  { id: 'stages', label: 'Stages' },
  { id: 'queued', label: 'Na fila' },
  { id: 'processing', label: 'Gerando' },
  { id: 'done', label: 'Pronto' },
];

function phaseFromStatus(status: string): Phase {
  if (status === 'processing' || status === 'submitted') return 'processing';
  return 'queued';
}

const PHASE_COPY: Record<Phase, string> = {
  stages: 'Gerando as imagens das stages…',
  queued: 'Seu vídeo entrou na fila.',
  processing: 'Gerando o vídeo. Costuma levar alguns minutos.',
  done: 'Vídeo pronto.',
  failed: 'A geração falhou.',
};

function FollowPanel({ track, onDismiss }: { track: VideoTrack; onDismiss?: () => void }) {
  const steps = track.withStages ? STEPS : STEPS.filter((s) => s.id !== 'stages');
  const current = steps.findIndex((s) => s.id === track.phase);
  const finished = track.phase === 'done' || track.phase === 'failed';
  return (
    <section className={`explore-follow is-${track.phase}`} role="status" aria-label={`Acompanhamento de ${track.title}`}>
      <header className="explore-follow-head">
        <strong>{track.title}</strong>
        {onDismiss && finished && (
          <button type="button" className="explore-icon-btn" aria-label="Fechar aviso" onClick={onDismiss}>
            <X size={16} aria-hidden />
          </button>
        )}
      </header>
      {track.phase !== 'failed' && (
        <ol className="ex-steps">
          {steps.map((step, i) => {
            const state = track.phase === 'done' || i < current ? 'is-done' : i === current ? 'is-current' : '';
            return (
              <li key={step.id} className={state} aria-current={i === current && track.phase !== 'done' ? 'step' : undefined}>
                {step.label}
              </li>
            );
          })}
        </ol>
      )}
      <p className={track.phase === 'failed' ? 'explore-error' : 'explore-follow-text'}>
        {track.phase === 'failed'
          ? (track.error || PHASE_COPY.failed)
          : track.phase === 'stages' && track.stageTotal
            ? `Gerando as imagens das stages… ${track.stageReady || 0}/${track.stageTotal}`
            : PHASE_COPY[track.phase]}
      </p>
      {track.url ? (
        <>
          <video src={track.url} controls playsInline />
          <Link className="explore-btn" to="/galeria">Abrir na galeria</Link>
        </>
      ) : track.phase !== 'failed' ? (
        <div className="sk explore-follow-sk" aria-hidden />
      ) : null}
      <p className="explore-note">{track.credits} créditos debitados. O vídeo também entra na galeria da organização.</p>
    </section>
  );
}

const SORTS: { id: FeedSort; label: string }[] = [
  { id: 'recent', label: 'Recentes' },
  { id: 'views', label: 'Mais vistos' },
  { id: 'likes', label: 'Mais curtidos' },
  { id: 'saved', label: 'Salvos' },
];

function isSort(value: string | null): value is FeedSort {
  return value === 'recent' || value === 'views' || value === 'likes' || value === 'saved';
}

function formatCount(n: number): string {
  return new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
}

function pluralize(n: number, one: string, many: string): string {
  return `${formatCount(n)} ${n === 1 ? one : many}`;
}

export default function ExploreGallery() {
  const { user } = useAuth();
  const { org, refreshOrg } = useOrg();
  const [params, setParams] = useSearchParams();
  const sortParam = params.get('ordem');
  const sort: FeedSort = isSort(sortParam) ? sortParam : 'recent';
  const themeSlug = params.get('tema');
  const [search, setSearch] = useState(params.get('q') || '');
  const [debounced, setDebounced] = useState(params.get('q') || '');
  const [categories, setCategories] = useState<ExploreCategory[]>([]);
  const [catsReady, setCatsReady] = useState(false);
  const [items, setItems] = useState<ExploreTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [likes, setLikes] = useState<Set<string>>(new Set());
  const [saves, setSaves] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState<ExploreTemplate | null>(null);
  const [track, setTrack] = useState<VideoTrack | null>(null);
  const [active, setActive] = useState<string[]>([]);
  const ratios = useRef(new Map<string, number>());
  const trigger = useRef<HTMLElement | null>(null);
  const reduced = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  const categoryId = useMemo(
    () => (themeSlug ? categories.find((c) => c.slug === themeSlug)?.id || null : null),
    [categories, themeSlug],
  );

  const patchParams = useCallback((patch: Record<string, string | null>) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([key, value]) => {
        if (value) next.set(key, value); else next.delete(key);
      });
      return next;
    }, { replace: true });
  }, [setParams]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebounced(search);
      patchParams({ q: search.trim() || null });
    }, 250);
    return () => window.clearTimeout(t);
  }, [search, patchParams]);

  useEffect(() => {
    void fetchCategories()
      .then(setCategories)
      .catch(() => undefined)
      .finally(() => setCatsReady(true));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await fetchPublishedTemplates({
        categoryId,
        sort,
        search: debounced,
        userId: user?.id || null,
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível carregar a galeria.');
    } finally {
      setLoading(false);
    }
  }, [categoryId, sort, debounced, user?.id]);

  useEffect(() => {
    if (!catsReady) return;
    void load();
  }, [load, catsReady]);

  useEffect(() => {
    if (!track?.jobId || track.phase === 'done' || track.phase === 'failed') return;
    let cancel = false;
    void waitForJob(track.jobId, {
      timeoutMs: 60 * 60 * 1000,
      onStatus: (job) => {
        if (cancel || job.status === 'completed' || job.status === 'failed') return;
        const phase = phaseFromStatus(job.status);
        setTrack((prev) => (prev && prev.jobId === job.id && prev.phase !== phase ? { ...prev, phase } : prev));
      },
    }).then((job) => {
      if (cancel) return;
      setTrack((prev) => (prev && prev.jobId === job.id
        ? { ...prev, phase: 'done', url: toMediaUrl(job.result_url), error: null }
        : prev));
    }).catch((err) => {
      if (cancel) return;
      const message = err instanceof Error ? err.message : 'A geração falhou.';
      setTrack((prev) => (prev ? { ...prev, phase: 'failed', error: message } : prev));
    });
    return () => { cancel = true; };
  }, [track?.jobId, track?.phase]);

  useEffect(() => {
    if (!user) return;
    void fetchMyFlags(user.id).then((flags) => {
      setLikes(flags.likes);
      setSaves(flags.saves);
    }).catch(() => undefined);
  }, [user]);

  const recompute = useCallback(() => {
    if (reduced || document.hidden) {
      setActive([]);
      return;
    }
    const next = Array.from(ratios.current.entries())
      .filter(([, ratio]) => ratio >= 0.65)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([id]) => id);
    setActive((prev) => (prev.join() === next.join() ? prev : next));
  }, [reduced]);

  useEffect(() => {
    const onVis = () => recompute();
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [recompute]);

  const reportRatio = useCallback((id: string, ratio: number) => {
    ratios.current.set(id, ratio);
    recompute();
  }, [recompute]);

  const categoryLabel = useCallback((id: string | null) => {
    return categories.find((c) => c.id === id)?.label || '';
  }, [categories]);

  const openItem = (item: ExploreTemplate) => {
    trigger.current = document.activeElement as HTMLElement | null;
    setOpen(item);
  };

  const closeItem = useCallback(() => {
    setOpen(null);
    window.setTimeout(() => trigger.current?.focus(), 0);
  }, []);

  const filtering = Boolean(debounced.trim() || categoryId || sort === 'saved');
  const clearFilters = () => {
    setSearch('');
    setDebounced('');
    patchParams({ q: null, tema: null, ordem: null });
  };

  const showFeature = !loading && !error && items.length >= 3 && !filtering;
  const feedKey = useMemo(() => items.map((row) => row.id).join('|'), [items]);
  // One random highlight per feed load; keep it while likes/views update the same set.
  const featureId = useMemo(() => {
    if (!showFeature) return null;
    const pool = items.filter((row) => row.preview_video_url);
    const list = pool.length ? pool : items;
    if (!list.length) return null;
    return list[Math.floor(Math.random() * list.length)]?.id || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-roll only when the published set changes
  }, [showFeature, feedKey]);
  const featureItem = featureId ? items.find((row) => row.id === featureId) || null : null;
  const gridItems = featureItem ? items.filter((row) => row.id !== featureItem.id) : items;
  const firstLoad = loading && items.length === 0;
  const dockVisible = Boolean(track) && (!open || open.id !== track?.templateId);

  const renderCard = (item: ExploreTemplate) => (
    <ExploreCard
      key={item.id}
      item={item}
      category={categoryLabel(item.category_id)}
      playing={!reduced && active.includes(item.id) && open?.id !== item.id}
      onRatio={reportRatio}
      onOpen={() => openItem(item)}
    />
  );

  return (
    <div className="explore-page">
      <header className="explore-hero">
        <h1>Explorar</h1>
        <p className="explore-lead">Escolha um vídeo pronto, troque o nome e as imagens e gere o seu.</p>
      </header>

      <div className="explore-sticky">
        <div className="explore-toolbar">
          <label className="explore-search">
            <Search size={16} aria-hidden />
            <input
              type="search"
              name="q"
              value={search}
              autoComplete="off"
              spellCheck={false}
              placeholder="Buscar por título…"
              aria-label="Buscar por título"
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <div className="explore-seg" role="group" aria-label="Ordenar por">
            {SORTS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={sort === item.id ? 'is-on' : ''}
                aria-pressed={sort === item.id}
                onClick={() => patchParams({ ordem: item.id === 'recent' ? null : item.id })}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
        {!catsReady ? (
          <ChipsSkeleton />
        ) : (
          <div className="explore-chips" role="group" aria-label="Temas">
            <button
              type="button"
              className={`explore-chip ${categoryId === null ? 'is-on' : ''}`}
              aria-pressed={categoryId === null}
              onClick={() => patchParams({ tema: null })}
            >
              Todos
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                className={`explore-chip ${categoryId === cat.id ? 'is-on' : ''}`}
                aria-pressed={categoryId === cat.id}
                onClick={() => patchParams({ tema: cat.slug })}
              >
                {cat.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <p className="ex-sr" aria-live="polite">
        {!loading && !error ? pluralize(items.length, 'modelo encontrado', 'modelos encontrados') : ''}
      </p>

      {error && (
        <div className="explore-state" role="alert">
          <p className="explore-state-title">Não foi possível carregar</p>
          <p className="explore-note">{error}</p>
          <button type="button" className="explore-btn" onClick={() => void load()}>Tentar de novo</button>
        </div>
      )}

      {firstLoad && !error && <GallerySkeleton withFeature={!filtering} />}

      {!loading && !error && items.length === 0 && (
        <div className="explore-state">
          <Clapperboard size={28} aria-hidden />
          {filtering ? (
            <>
              <p className="explore-state-title">
                {sort === 'saved' ? 'Nenhum modelo salvo' : 'Nenhum modelo para esse filtro'}
              </p>
              <p className="explore-note">
                {sort === 'saved'
                  ? 'Toque em Salvar num modelo para guardá-lo aqui.'
                  : 'Tente outro termo ou volte para todos os temas.'}
              </p>
              <button type="button" className="explore-btn" onClick={clearFilters}>Limpar filtros</button>
            </>
          ) : (
            <>
              <p className="explore-state-title">Ainda não há modelos publicados</p>
              <p className="explore-note">Quando o estúdio publicar um vídeo, ele aparece aqui.</p>
            </>
          )}
        </div>
      )}

      {featureItem && (
        <ExploreFeature
          item={featureItem}
          category={categoryLabel(featureItem.category_id)}
          playing={!reduced && active.includes(featureItem.id) && open?.id !== featureItem.id}
          onRatio={reportRatio}
          onOpen={() => openItem(featureItem)}
        />
      )}

      {!firstLoad && gridItems.length > 0 && (
        <div className={`explore-grid ${loading ? 'is-busy' : ''}`} aria-busy={loading}>
          {gridItems.map(renderCard)}
        </div>
      )}

      {dockVisible && track && (
        <div className="explore-dock">
          <FollowPanel track={track} onDismiss={() => setTrack(null)} />
        </div>
      )}

      {open && user && (
        <ExploreViewer
          item={open}
          category={categoryLabel(open.category_id)}
          liked={likes.has(open.id)}
          saved={saves.has(open.id)}
          balance={Number(org?.available_credits) || 0}
          orgId={org?.id || ''}
          userId={user.id}
          track={track && track.templateId === open.id ? track : null}
          onQueued={(next) => setTrack(next)}
          onClose={closeItem}
          onLiked={(on) => {
            setLikes((prev) => {
              const next = new Set(prev);
              if (on) next.add(open.id); else next.delete(open.id);
              return next;
            });
            setItems((prev) => prev.map((row) => row.id === open.id
              ? { ...row, like_count: Math.max(0, row.like_count + (on ? 1 : -1)) }
              : row));
          }}
          onSaved={(on) => {
            setSaves((prev) => {
              const next = new Set(prev);
              if (on) next.add(open.id); else next.delete(open.id);
              return next;
            });
            if (!on && sort === 'saved') {
              setItems((prev) => prev.filter((row) => row.id !== open.id));
            }
          }}
          onCharged={() => { void refreshOrg(false); }}
        />
      )}
    </div>
  );
}

/** Vídeo da prévia: mostra o primeiro quadro parado e só toca quando `playing`. */
function PreviewMedia({ item, playing }: { item: ExploreTemplate; playing: boolean }) {
  const [ready, setReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const poster = item.poster_url || '';

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (playing) void el.play().catch(() => undefined);
    else el.pause();
  }, [playing]);

  return (
    <>
      {!ready && <span className="sk explore-media-fill" aria-hidden />}
      {poster && (
        <img
          src={poster}
          alt=""
          width={400}
          height={500}
          loading="lazy"
          decoding="async"
          className={ready ? '' : 'is-loading'}
          onLoad={() => setReady(true)}
        />
      )}
      {item.preview_video_url && (
        <video
          ref={videoRef}
          src={`${item.preview_video_url}#t=0.1`}
          poster={poster || undefined}
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden
          tabIndex={-1}
          onLoadedData={() => setReady(true)}
        />
      )}
    </>
  );
}

function useVisibility(id: string, onRatio: (id: string, ratio: number) => void) {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      onRatio(id, entry.isIntersecting ? entry.intersectionRatio : 0);
    }, { threshold: [0, 0.65, 1] });
    obs.observe(el);
    return () => {
      onRatio(id, 0);
      obs.disconnect();
    };
  }, [id, onRatio]);
  return ref;
}

function CreditChip({ item }: { item: ExploreTemplate }) {
  const credits = quoteTemplateCredits(item);
  if (credits == null) return null;
  return <span className="explore-credit">{credits}&nbsp;cr</span>;
}

function ExploreFeature({
  item,
  category,
  playing,
  onRatio,
  onOpen,
}: {
  item: ExploreTemplate;
  category: string;
  playing: boolean;
  onRatio: (id: string, ratio: number) => void;
  onOpen: () => void;
}) {
  const ref = useVisibility(item.id, onRatio);
  const credits = quoteTemplateCredits(item);
  return (
    <article className="explore-feature">
      <button type="button" className="explore-feature-media" ref={ref} onClick={onOpen} aria-label={`Abrir ${item.title}`}>
        <PreviewMedia item={item} playing={playing} />
        <span className="explore-duration">{item.duration_seconds}&nbsp;s</span>
      </button>
      <div className="explore-feature-info">
        {category && <span className="explore-tag">{category}</span>}
        <h2>{item.title}</h2>
        {item.description && <p className="explore-feature-desc">{item.description}</p>}
        <p className="explore-facts">
          <span>{pluralize(item.view_count, 'visualização', 'visualizações')}</span>
          <span><Heart size={13} aria-hidden /> {formatCount(item.like_count)}</span>
        </p>
        <button type="button" className="explore-btn is-primary is-lg" onClick={onOpen}>
          <Play size={15} fill="currentColor" aria-hidden />
          Ver e criar igual{credits != null ? ` · ${credits} cr` : ''}
        </button>
      </div>
    </article>
  );
}

function ExploreCard({
  item,
  category,
  playing,
  onRatio,
  onOpen,
}: {
  item: ExploreTemplate;
  category: string;
  playing: boolean;
  onRatio: (id: string, ratio: number) => void;
  onOpen: () => void;
}) {
  const ref = useVisibility(item.id, onRatio);
  return (
    <button type="button" className="explore-card" ref={ref} onClick={onOpen}>
      <div className="explore-media">
        <PreviewMedia item={item} playing={playing} />
        <CreditChip item={item} />
        <span className="explore-duration">{item.duration_seconds}&nbsp;s</span>
        {!playing && (
          <span className="explore-play" aria-hidden>
            <Play size={14} fill="currentColor" />
          </span>
        )}
      </div>
      <div className="explore-card-meta">
        <strong>{item.title}</strong>
        <span className="explore-card-sub">
          <span className="explore-card-cat">{category || 'Sem tema'}</span>
          <span className="explore-likes" title={pluralize(item.like_count, 'curtida', 'curtidas')}>
            <Heart size={12} aria-hidden />
            <span className="ex-sr">{pluralize(item.like_count, 'curtida', 'curtidas')}</span>
            <span aria-hidden>{formatCount(item.like_count)}</span>
          </span>
        </span>
      </div>
    </button>
  );
}

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function ExploreViewer({
  item,
  category,
  liked,
  saved,
  balance,
  orgId,
  userId,
  track,
  onQueued,
  onClose,
  onLiked,
  onSaved,
  onCharged,
  seedTexts,
  seedImageUrls,
  seedDuration,
  openForm,
  onCanvasGenerated,
}: {
  item: ExploreTemplate;
  category: string;
  liked: boolean;
  saved: boolean;
  balance: number;
  orgId: string;
  userId: string;
  track: VideoTrack | null;
  onQueued: (track: VideoTrack) => void;
  onClose: () => void;
  onLiked: (on: boolean) => void;
  onSaved: (on: boolean) => void;
  onCharged: () => void;
  /** Text the agent already heard, keyed by template slot. */
  seedTexts?: Record<string, string>;
  /** Flow photos, in order. The first ones fill the image slots. */
  seedImageUrls?: string[];
  /** Duration the agent picked. Used when the template lets the person change it. */
  seedDuration?: number;
  /** Open the personalize form as soon as the slots load. */
  openForm?: boolean;
  /** Criar igual returns to the canvas instead of staying on Explorar. */
  onCanvasGenerated?: (result: CanvasRemixResult) => void;
}) {
  const [slots, setSlots] = useState<ExploreSlot[]>([]);
  const [slotsReady, setSlotsReady] = useState(false);
  const [texts, setTexts] = useState<Record<string, string>>({});
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [seeded, setSeeded] = useState<Record<string, string>>({});
  const seededOnce = useRef(false);
  const seedTextsRef = useRef(seedTexts);
  const seedImagesRef = useRef(seedImageUrls);
  const openFormRef = useRef(openForm);
  seedTextsRef.current = seedTexts;
  seedImagesRef.current = seedImageUrls;
  openFormRef.current = openForm;
  const [showForm, setShowForm] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const followRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const lockedModel = item.lock_model !== false;
  const seededModel = getVideoModelById(item.video_model_id);
  const templateMinDuration = Math.max(
    seededModel?.durationMin || 1,
    Number(item.min_duration_seconds) > 0 ? Math.round(Number(item.min_duration_seconds)) : 0,
  );
  const seededDuration = !lockedModel && seedDuration
    ? Math.max(
      templateMinDuration,
      Math.min(seededModel?.durationMax || item.duration_seconds, Math.round(seedDuration)),
    )
    : !lockedModel
    ? Math.max(templateMinDuration, item.duration_seconds)
    : item.duration_seconds;
  const [pick, setPick] = useState<VideoModelPickerValue>({
    videoModelId: item.video_model_id,
    resolutionId: item.resolution_id,
    durationSeconds: seededDuration,
    generateAudio: item.generate_audio,
  });
  const closeRef = useRef<HTMLButtonElement>(null);
  const locked = lockedModel;
  const enableStages = item.enable_stages === true;
  const [imageModelId, setImageModelId] = useState(
    () => item.image_model_id || defaultImageModel().id,
  );
  const [aspectMode, setAspectMode] = useState<ExploreAspectMode>('auto');
  const [imageSizes, setImageSizes] = useState<{ width: number; height: number }[]>([]);
  const modality = slots.some((slot) => slot.slot_type !== 'text') || enableStages ? 'i2v' : 't2v';
  const activeModel = getVideoModelById(locked ? item.video_model_id : pick.videoModelId);
  const qualityChoices = locked && activeModel && activeModel.resolutions.length > 1 ? activeModel.resolutions : [];
  const credits = useMemo(() => quoteTemplateCredits({
    video_model_id: locked ? item.video_model_id : pick.videoModelId,
    resolution_id: pick.resolutionId,
    duration_seconds: locked ? item.duration_seconds : pick.durationSeconds,
    generate_audio: locked ? item.generate_audio : Boolean(pick.generateAudio),
    enable_stages: enableStages,
    stage_count: item.stage_count,
    // Stages always let the person pick the image model (template value is the default).
    image_model_id: enableStages ? imageModelId : item.image_model_id,
  }), [item, locked, pick, enableStages, imageModelId]);

  useEffect(() => {
    let cancel = false;
    closeRef.current?.focus();
    void recordTemplateView(item.id);
    setSlotsReady(false);
    setAspectMode('auto');
    setImageSizes([]);
    seededOnce.current = false;
    void fetchHomeSlots(item.id).then((rows) => {
      if (cancel) return;
      setSlots(rows);
      if (!seededOnce.current) {
        seededOnce.current = true;
        if (seedTextsRef.current) setTexts(seedTextsRef.current);
        const flow = assignFlowImages(rows, seedImagesRef.current || []);
        setSeeded(flow.seeded);
        if (openFormRef.current || seedImagesRef.current?.length || seedTextsRef.current) setShowForm(rows.length > 0);
      }
      setSlotsReady(true);
    }).catch((err) => {
      if (cancel) return;
      setSlotsReady(true);
      setError(err instanceof Error ? err.message : 'Não foi possível abrir o modelo.');
    });
    return () => { cancel = true; };
  }, [item.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (confirm) return;
      if (e.key === 'Escape' && !busy) {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !cardRef.current) return;
      const nodes = Array.from(cardRef.current.querySelectorAll<HTMLElement>(FOCUSABLE))
        .filter((el) => el.offsetParent !== null);
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, confirm, onClose]);

  const hasInputImages = slots.some((slot) => slot.slot_type !== 'text' && (files[slot.key] || seeded[slot.key]));
  const outputAspect = aspectFromMode(aspectMode, imageSizes, item.aspect_ratio);

  useEffect(() => {
    let cancel = false;
    const filesToRead: File[] = [];
    const urls: string[] = [];
    for (const slot of slots) {
      if (slot.slot_type === 'text') continue;
      const file = files[slot.key];
      if (file) filesToRead.push(file);
      else if (seeded[slot.key]) urls.push(seeded[slot.key]);
    }
    if (!filesToRead.length && !urls.length) {
      setImageSizes([]);
      return undefined;
    }
    void (async () => {
      const sizes: { width: number; height: number }[] = [];
      for (const file of filesToRead) {
        const url = URL.createObjectURL(file);
        try {
          sizes.push(await readImageSize(url));
        } catch {
          /* unreadable still — the server measures the uploaded file */
        } finally {
          URL.revokeObjectURL(url);
        }
      }
      for (const url of urls) {
        try {
          sizes.push(await readImageSize(url));
        } catch {
          /* gallery URL without dimensions */
        }
      }
      if (!cancel) setImageSizes(sizes);
    })();
    return () => { cancel = true; };
  }, [slots, files, seeded]);

  const missing = slots.some((slot) => {
    if (!slot.required) return false;
    if (slot.slot_type === 'text') return !texts[slot.key]?.trim();
    return !files[slot.key] && !seeded[slot.key];
  });

  const toggle = async (kind: 'like' | 'save') => {
    const on = kind === 'like' ? !liked : !saved;
    try {
      await setFlag(kind === 'like' ? 'video_template_likes' : 'video_template_saves', userId, item.id, on);
      if (kind === 'like') onLiked(on);
      else onSaved(on);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível salvar.');
    }
  };

  const submit = async () => {
    if (!orgId) {
      setError('Organização ainda não carregou.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const values: Record<string, string> = {};
      for (const slot of slots) {
        if (slot.slot_type === 'text') {
          values[slot.key] = texts[slot.key] || '';
          continue;
        }
        const file = files[slot.key];
        if (file) {
          const asset = await uploadToGallery(orgId, file, { title: slot.label });
          values[slot.key] = asset.video_url;
          continue;
        }
        if (seeded[slot.key]) values[slot.key] = seeded[slot.key];
      }
      const model = locked ? {
        videoModelId: item.video_model_id,
        resolutionId: pick.resolutionId,
        durationSeconds: item.duration_seconds,
        generateAudio: item.generate_audio,
      } : {
        videoModelId: pick.videoModelId,
        resolutionId: pick.resolutionId,
        durationSeconds: pick.durationSeconds,
        generateAudio: Boolean(pick.generateAudio),
      };
      const chosen = orderedImageSlots(slots).flatMap((slot) => {
        const url = mediaUrl(values[slot.key]);
        return url ? [{ url, label: slot.label }] : [];
      });
      const includeUserImages = !enableStages || item.stages_include_user_images !== false;
      const canvasBase = (): Omit<CanvasRemixResult, 'jobId' | 'credits' | 'phase'> => ({
        title: item.title,
        aspectRatio: outputAspect,
        lockModel: locked,
        model,
        imageUrls: chosen.map((row) => row.url),
        imageLabels: chosen.map((row) => row.label),
        includeUserImages,
      });

      // Canvas: leave the viewer immediately and keep working in the background.
      if (onCanvasGenerated) {
        setConfirm(false);
        onClose();
        onCanvasGenerated({
          ...canvasBase(),
          jobId: '',
          credits: 0,
          phase: 'preparing',
          awaitingStages: enableStages,
          ...(enableStages ? {
            stageCount: Math.max(2, Math.min(12, Math.round(Number(item.stage_count) || 7))),
          } : {}),
        });
        let stageBatchId: string | undefined;
        let stageUrls: string[] = [];
        let charged = 0;
        try {
          if (enableStages) {
            const stages = await remixTemplate({
              organization_id: orgId,
              template_id: item.id,
              intent: 'stages',
              values,
              image_model_id: imageModelId,
              aspect_mode: aspectMode,
            });
            stageBatchId = stages.stage_batch_id;
            charged += stages.credits_charged;
            const ids = stages.job_ids || [];
            const { urls, failed } = await waitForStageJobs(ids, {
              timeoutMs: STAGE_JOBS_TIMEOUT_MS,
              onPartial: (partial) => {
                onCanvasGenerated({
                  ...canvasBase(),
                  jobId: '',
                  credits: charged,
                  phase: 'preparing',
                  awaitingStages: true,
                  stageCount: ids.length,
                  stageUrls: partial.map((u) => u || ''),
                });
              },
            });
            if (failed || urls.length < ids.length) {
              throw new Error(`Só ${urls.length}/${ids.length} stages concluíram. Tente de novo.`);
            }
            stageUrls = urls;
          }
          const result = await remixTemplate({
            organization_id: orgId,
            template_id: item.id,
            intent: 'remix',
            values,
            stage_batch_id: stageBatchId,
            model: {
              video_model_id: model.videoModelId,
              resolution_id: model.resolutionId,
              duration_seconds: model.durationSeconds,
              generate_audio: model.generateAudio,
            },
            aspect_mode: aspectMode,
          });
          if (!result.job_id) throw new Error('Não foi possível enfileirar o vídeo.');
          charged += result.credits_charged;
          onCanvasGenerated({
            ...canvasBase(),
            jobId: result.job_id,
            credits: charged,
            aspectRatio: result.aspect_ratio || outputAspect,
            ...(stageUrls.length ? { stageUrls } : {}),
            phase: 'ready',
          });
          onCharged();
        } catch (err) {
          onCanvasGenerated({
            ...canvasBase(),
            jobId: '',
            credits: charged,
            phase: 'failed',
            error: err instanceof Error ? err.message : 'Não foi possível criar o vídeo.',
          });
        }
        return;
      }

      let stageBatchId: string | undefined;
      let charged = 0;
      if (enableStages) {
        onQueued({
          jobId: '',
          templateId: item.id,
          title: item.title,
          credits: 0,
          phase: 'stages',
          url: null,
          error: null,
          withStages: true,
        });
        setShowForm(false);
        setConfirm(false);
        const stages = await remixTemplate({
          organization_id: orgId,
          template_id: item.id,
          intent: 'stages',
          values,
          image_model_id: imageModelId,
          aspect_mode: aspectMode,
        });
        stageBatchId = stages.stage_batch_id;
        charged += stages.credits_charged;
        onQueued({
          jobId: '',
          templateId: item.id,
          title: item.title,
          credits: charged,
          phase: 'stages',
          url: null,
          error: null,
          withStages: true,
        });
        const ids = stages.job_ids || [];
        const { urls, failed } = await waitForStageJobs(ids, {
          timeoutMs: STAGE_JOBS_TIMEOUT_MS,
          onPartial: (partial) => {
            const ready = partial.filter((u): u is string => Boolean(u)).length;
            onQueued({
              jobId: '',
              templateId: item.id,
              title: item.title,
              credits: charged,
              phase: 'stages',
              url: null,
              error: null,
              withStages: true,
              stageReady: ready,
              stageTotal: ids.length,
            });
          },
        });
        if (failed || urls.length < ids.length) {
          throw new Error(`Só ${urls.length}/${ids.length} stages concluíram. Tente de novo.`);
        }
      }
      const result = await remixTemplate({
        organization_id: orgId,
        template_id: item.id,
        intent: 'remix',
        values,
        stage_batch_id: stageBatchId,
        model: {
          video_model_id: model.videoModelId,
          resolution_id: model.resolutionId,
          duration_seconds: model.durationSeconds,
          generate_audio: model.generateAudio,
        },
        aspect_mode: aspectMode,
      });
      if (!result.job_id) throw new Error('Não foi possível enfileirar o vídeo.');
      charged += result.credits_charged;
      onQueued({
        jobId: result.job_id,
        templateId: item.id,
        title: item.title,
        credits: charged,
        phase: 'queued',
        url: null,
        error: null,
        withStages: enableStages,
      });
      setShowForm(false);
      setConfirm(false);
      onCharged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível criar o vídeo.');
      if (enableStages) {
        onQueued({
          jobId: '',
          templateId: item.id,
          title: item.title,
          credits: 0,
          phase: 'failed',
          url: null,
          error: err instanceof Error ? err.message : 'Não foi possível criar o vídeo.',
          withStages: true,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const creditLabel = credits == null ? 'Criar igual' : `Criar igual · ${credits} cr`;

  useEffect(() => {
    followRef.current?.scrollIntoView({ block: 'nearest' });
  }, [track?.phase, track?.url]);

  return (
    <>
      <div className="explore-viewer" role="presentation" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
        <div className="explore-viewer-card" role="dialog" aria-modal="true" aria-labelledby="explore-title" ref={cardRef}>
          <button type="button" className="explore-close" aria-label="Fechar" onClick={onClose} ref={closeRef}>
            <X size={18} aria-hidden />
          </button>
          <div className="explore-viewer-stage">
            {item.preview_video_url ? (
              <video
                src={item.preview_video_url}
                controls
                playsInline
                autoPlay
                loop
                muted
                poster={item.poster_url || undefined}
                aria-label={`Prévia: ${item.title}`}
              />
            ) : (
              <div className="sk explore-media-fill" aria-hidden />
            )}
          </div>
          <div className="explore-viewer-panel">
            <div className="explore-viewer-scroll">
              {category && <span className="explore-tag">{category}</span>}
              <h2 id="explore-title">{item.title}</h2>
              <p className="explore-facts">
                {activeModel && <span>{activeModel.label}</span>}
                <span>{item.duration_seconds}&nbsp;s</span>
                <span>{item.aspect_ratio}</span>
                <span>{pluralize(item.view_count, 'visualização', 'visualizações')}</span>
              </p>
              {item.description && <p className="explore-desc">{item.description}</p>}

              {slotsReady && (
                <ExploreAspectField
                  mode={aspectMode}
                  templateAspect={item.aspect_ratio}
                  sizes={imageSizes}
                  hasImages={hasInputImages}
                  disabled={busy}
                  onChange={setAspectMode}
                />
              )}

              {track && <div ref={followRef}><FollowPanel track={track} /></div>}

              {!slotsReady && <ViewerPanelSkeleton />}

              {showForm && slotsReady && (
                <form
                  id="explore-remix-form"
                  className="explore-form"
                  onSubmit={(e) => { e.preventDefault(); if (!missing) setConfirm(true); }}
                >
                  <h3>Personalize o seu vídeo</h3>
                  {enableStages && (
                    <p className="explore-note">
                      O agente divide o seu brief em {Math.max(2, Math.min(12, Number(item.stage_count) || 7))} prompts (1 por stage)
                      {item.adapt_stage_prompt ? ', combinando com as fotos' : ''}
                      , e envia cada prompt + as fotos como referência para o modelo de imagem.
                      {item.stages_include_user_images
                        ? ' No vídeo entram as stages geradas e também as suas fotos.'
                        : ' No vídeo entram só as stages geradas (suas fotos servem de referência).'}
                    </p>
                  )}
                  {item.adapt_prompt && (
                    <p className="explore-note">O roteiro se adapta às imagens{enableStages ? ' das stages' : ' que você enviar'}.</p>
                  )}
                  {enableStages && (
                    <div className="ex-field">
                      <label htmlFor="explore-image-model">Modelo de imagem</label>
                      <ImageModelPicker
                        compact
                        value={imageModelId}
                        onChange={setImageModelId}
                        aria-label="Modelo de imagem das stages"
                      />
                    </div>
                  )}
                  {locked && activeModel && (
                    <div className="ex-field">
                      <span className="ex-field-label">Modelo</span>
                      <p className="explore-model">{activeModel.label}</p>
                    </div>
                  )}
                  {qualityChoices.length > 0 && (
                    <div className="ex-field">
                      <label htmlFor="explore-quality">Qualidade</label>
                      <select
                        id="explore-quality"
                        value={pick.resolutionId}
                        onChange={(e) => setPick((prev) => ({ ...prev, resolutionId: e.target.value }))}
                      >
                        {qualityChoices.map((row) => (
                          <option key={row.id} value={row.id}>{row.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {!locked && (
                    <VideoModelPicker
                      compact
                      modality={modality}
                      value={pick}
                      durationSeconds={pick.durationSeconds}
                      durationFloorSeconds={templateMinDuration}
                      minDurationSeconds={templateMinDuration}
                      onDurationChange={(seconds) => setPick((prev) => ({ ...prev, durationSeconds: seconds }))}
                      onChange={(next) => {
                        const model = getVideoModelById(next.videoModelId);
                        if (model && requiresDrivingAudio(model)) {
                          setError('Avatares com áudio não entram no Criar igual. Escolha outro modelo.');
                          return;
                        }
                        setError(null);
                        setPick(next);
                      }}
                    />
                  )}
                  {slots.length === 0 && locked && <p className="explore-note">Este vídeo não pede dados extras.</p>}
                  {slots.map((slot) => (
                    <div key={slot.id} className="ex-field">
                      <label htmlFor={`slot-${slot.id}`}>
                        {slot.label}
                        {slot.required ? <span className="ex-req" aria-label="obrigatório"> *</span> : <span className="ex-opt"> (opcional)</span>}
                      </label>
                      {slot.slot_type === 'text' ? (
                        <input
                          id={`slot-${slot.id}`}
                          name={slot.key}
                          type="text"
                          maxLength={400}
                          autoComplete="off"
                          value={texts[slot.key] || ''}
                          placeholder="Digite aqui…"
                          onChange={(e) => setTexts((prev) => ({ ...prev, [slot.key]: e.target.value }))}
                        />
                      ) : (
                        <ExploreImageDrop
                          id={`slot-${slot.id}`}
                          name={slot.key}
                          file={files[slot.key] || null}
                          seededUrl={seeded[slot.key] || ''}
                          onFile={(file) => setFiles((prev) => ({ ...prev, [slot.key]: file }))}
                          onClearSeed={() => setSeeded((prev) => ({ ...prev, [slot.key]: '' }))}
                        />
                      )}
                    </div>
                  ))}
                </form>
              )}
              {error && <p className="explore-error" role="alert">{error}</p>}
            </div>

            <div className="explore-viewer-foot">
              <button
                type="button"
                className="explore-btn is-toggle"
                aria-pressed={liked}
                onClick={() => void toggle('like')}
              >
                <Heart size={16} fill={liked ? 'currentColor' : 'none'} aria-hidden /> Curtir
              </button>
              <button
                type="button"
                className="explore-btn is-toggle"
                aria-pressed={saved}
                onClick={() => void toggle('save')}
              >
                <Bookmark size={16} fill={saved ? 'currentColor' : 'none'} aria-hidden /> Salvar
              </button>
              {showForm ? (
                <button
                  type="submit"
                  form="explore-remix-form"
                  className="explore-btn is-primary is-grow"
                  disabled={busy || missing || !orgId || credits == null}
                >
                  {busy ? 'Enviando…' : creditLabel}
                </button>
              ) : (
                <button
                  type="button"
                  className="explore-btn is-primary is-grow"
                  disabled={credits == null || !slotsReady}
                  onClick={() => { if (slots.length === 0 && locked && qualityChoices.length === 0) setConfirm(true); else setShowForm(true); }}
                >
                  {creditLabel}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <CreditConfirmModal
        open={confirm}
        title="Criar igual"
        subtitle={[
          activeModel ? `Vídeo com ${activeModel.label}.` : '',
          aspectMode === 'vertical'
            ? 'O vídeo sai na vertical (9:16).'
            : aspectMode === 'horizontal'
            ? 'O vídeo sai na horizontal (16:9).'
            : 'A proporção segue as fotos. Sem foto, fica a do modelo.',
          item.adapt_prompt ? 'O roteiro se adapta às imagens e debita créditos de chat além do vídeo.' : 'O roteiro do criador não aparece.',
        ].filter(Boolean).join(' ')}
        credits={credits || 0}
        balance={balance}
        busy={busy}
        confirmDisabled={missing || !orgId}
        error={error}
        confirmLabel="Gerar vídeo"
        onCancel={() => { if (!busy) setConfirm(false); }}
        onConfirm={() => void submit()}
      />
    </>
  );
}

/** The same Criar igual dialog, opened from the canvas agent with the flow already filled in. */
export function CanvasExploreModal(props: {
  templateId: string;
  texts?: Record<string, string>;
  imageUrls: string[];
  durationSeconds?: number;
  onClose: () => void;
  onGenerated: (result: CanvasRemixResult) => void;
}) {
  const { user } = useAuth();
  const { org, refreshOrg } = useOrg();
  const [item, setItem] = useState<ExploreTemplate | null>(null);
  const [category, setCategory] = useState('');
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    setItem(null);
    setError(null);
    void (async () => {
      try {
        const [template, cats] = await Promise.all([
          fetchPublishedTemplate(props.templateId),
          fetchCategories(),
        ]);
        if (cancel) return;
        if (!template) {
          setError('Esse modelo não está mais publicado.');
          return;
        }
        setItem(template);
        setCategory(cats.find((row) => row.id === template.category_id)?.label || '');
        if (user) {
          const flags = await fetchMyFlags(user.id);
          if (cancel) return;
          setLiked(flags.likes.has(template.id));
          setSaved(flags.saves.has(template.id));
        }
      } catch (err) {
        if (!cancel) setError(err instanceof Error ? err.message : 'Não foi possível abrir o modelo.');
      }
    })();
    return () => { cancel = true; };
  }, [props.templateId, user]);

  if (!item || !user) {
    return (
      <div className="explore-viewer" role="presentation" onClick={(e) => { if (e.target === e.currentTarget) props.onClose(); }}>
        <div className="explore-viewer-card" role="dialog" aria-modal="true" aria-label="Modelo do Explorar">
          <button type="button" className="explore-close" aria-label="Fechar" onClick={props.onClose}>
            <X size={18} aria-hidden />
          </button>
          <div className="explore-viewer-panel">
            <p className={error ? 'explore-error' : 'explore-note'} role={error ? 'alert' : 'status'}>
              {error || 'Abrindo modelo…'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ExploreViewer
      item={item}
      category={category}
      liked={liked}
      saved={saved}
      balance={Number(org?.available_credits) || 0}
      orgId={org?.id || ''}
      userId={user.id}
      track={null}
      seedTexts={props.texts}
      seedImageUrls={props.imageUrls}
      seedDuration={props.durationSeconds}
      openForm
      onQueued={() => undefined}
      onClose={props.onClose}
      onLiked={setLiked}
      onSaved={setSaved}
      onCharged={() => { void refreshOrg(false); }}
      onCanvasGenerated={(result) => {
        void refreshOrg(false);
        props.onGenerated(result);
      }}
    />
  );
}
